import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import InfoTab from './tabelements/infotab/InfoTab';
import AccountStatus from './tabelements/statustab/AccountStatus';
import UserReminderTab from './tabelements/userstab/UserReminderTab';
import MediaTab from './tabelements/mediatab/MediaTab';
import ReminderTab from './tabelements/remindertab/ReminderTab';
import SettingsTab from './tabelements/featurestab/SettingsTab';
import LensTab from './tabelements/lensbrandtab/LensTab';
import UsersTab from './tabelements/userstab/UsersTab';
import TabContent from './TabContent';
import TabNavItem from './TabNavItem';
import ClinicUserAdd from './tabelements/userstab/ClinicUserAdd';
import ClinicUserSettings from './tabelements/userstab/ClinicUserSettings';
import CustomizationTab from './tabelements/customizationtab/CustomizationTab';
import SocialTab from './tabelements/socialtab/SocialTab';
import HistoryTab from './tabelements/historytab/HistoryTab';
import HoursOfOperation from './tabelements/hourstab/HoursOfOperation';
import ReputationTab from './tabelements/reputationtab/ReputationTab';
import BookingTab from './tabelements/bookingtab/BookingTab';
import StatsTab from './tabelements/statstab/StatsTab';
import SurveyTab from './tabelements/surveystab/SurveyTab';
import RecallsTab from './tabelements/recallstab/RecallsTab';
import SmsgatewayTab from './tabelements/smsgatewaytab/SmsgatewayTab';
import DocumentTab from './tabelements/documenttab/DocumentTab';
import PublicThemeTab from './tabelements/publicthemetab/PublicThemeTab';
import OptosysTab from './tabelements/optosystab/OptosysTab';
import clinicService from '../../../services/clinic.service';
import './clinics-modules.css';

const DEFAULT_TAB_ID = 'clinic-info-tab';
const DEFAULT_TAB_SLUG = 'info';

/** Share-friendly URL slugs for clinic module tabs (?tab=documents). */
const TAB_SLUG_BY_ID = {
    'clinic-info-tab': 'info',
    'clinic-documents-tab': 'documents',
    'clinic-status-tab': 'status',
    'clinic-history-tab': 'history',
    'clinic-optosys-tab': 'optosys',
    'clinic-hour-tab': 'hours',
    'clinic-booking-tab': 'bookings',
    'clinic-reminder-tab': 'reminder',
    'clinic-recalls-tab': 'recalls',
    'clinic-sms-gateway': 'sms-gateway',
    'clinic-reputation-tab': 'reputation',
    'clinic-surveys-tab': 'surveys',
    'clinic-users-tab': 'users',
    'clinic-user-reminder': 'user-reminder',
    'clinic-user-settings': 'user-settings',
    'clinic-user-add': 'user-add',
    'clinic-settings-tab': 'features',
    'clinic-customization-tab': 'customization',
    'clinic-public-theme-tab': 'public-theme',
    'clinic-lensbrand-tab': 'lens-brand',
    'clinic-social-tab': 'social',
    'clinic-media-tab': 'media',
    'clinic-stats-tab': 'stats',
};

const TAB_ID_BY_SLUG = Object.fromEntries(
    Object.entries(TAB_SLUG_BY_ID).map(([id, slug]) => [slug, id])
);

/** PMS-specific tabs: set `requiresPms` to show only when that PMS is selected. */
const NAV_GROUPS = [
    {
        label: 'Overview',
        items: [
            {
                id: 'clinic-info-tab',
                title: 'Info',
                icon: 'bi bi-info-circle',
                iconfill: 'bi bi-info-circle-fill',
            },
            {
                id: 'clinic-documents-tab',
                title: 'Documents',
                icon: 'bi bi-file-richtext',
                iconfill: 'bi bi-file-richtext-fill',
            },
            {
                id: 'clinic-status-tab',
                title: 'Account Status',
                icon: 'bi bi-clipboard2-pulse',
                iconfill: 'bi bi-clipboard2-pulse-fill',
            },
            {
                id: 'clinic-history-tab',
                title: 'History',
                icon: 'bi bi-clock-history',
                iconfill: 'bi bi-clock-history',
            },
            {
                id: 'clinic-optosys-tab',
                title: 'Optosys',
                icon: 'bi bi-hdd-network',
                iconfill: 'bi bi-hdd-network-fill',
                requiresPms: 'OPTOSYS',
            },
        ],
    },
    {
        label: 'Operations',
        items: [
            {
                id: 'clinic-hour-tab',
                title: 'Hours of Operations',
                icon: 'bi bi-clock',
                iconfill: 'bi bi-clock-fill',
            },
            {
                id: 'clinic-booking-tab',
                title: 'Bookings',
                icon: 'bi bi-calendar3-range',
                iconfill: 'bi bi-calendar3-range-fill',
            },
            {
                id: 'clinic-reminder-tab',
                title: 'Reminder',
                icon: 'bi bi-alarm',
                iconfill: 'bi bi-alarm-fill',
            },
            {
                id: 'clinic-recalls-tab',
                title: 'Recalls',
                icon: 'bi bi-repeat',
                iconfill: 'bi bi-repeat',
            },
        ],
    },
    {
        label: 'Communications',
        items: [
            {
                id: 'clinic-sms-gateway',
                title: 'SMS Gateway',
                icon: 'bi bi-chat-text',
                iconfill: 'bi bi-chat-text-fill',
            },
            {
                id: 'clinic-reputation-tab',
                title: 'Reputation',
                icon: 'bi bi-star',
                iconfill: 'bi bi-star-fill',
            },
            {
                id: 'clinic-surveys-tab',
                title: 'Surveys',
                icon: 'bi bi-clipboard-data',
                iconfill: 'bi bi-clipboard-data-fill',
            },
        ],
    },
    {
        label: 'Configuration',
        items: [
            {
                id: 'clinic-users-tab',
                title: 'Users',
                icon: 'bi bi-people',
                iconfill: 'bi bi-people-fill',
            },
            {
                id: 'clinic-settings-tab',
                title: 'Features',
                icon: 'bi bi-gear',
                iconfill: 'bi bi-gear-fill',
            },
            {
                id: 'clinic-customization-tab',
                title: 'Customization',
                icon: 'bi bi-house-gear',
                iconfill: 'bi bi-house-gear-fill',
            },
            {
                id: 'clinic-public-theme-tab',
                title: 'Public Theme',
                icon: 'bi bi-palette',
                iconfill: 'bi bi-palette-fill',
            },
            {
                id: 'clinic-lensbrand-tab',
                title: 'Lens Brand',
                icon: 'bi bi-eye',
                iconfill: 'bi bi-eye-fill',
            },
            {
                id: 'clinic-social-tab',
                title: 'Social Links',
                icon: 'bi bi-share',
                iconfill: 'bi bi-share-fill',
            },
        ],
    },
    {
        label: 'Media & Analytics',
        items: [
            {
                id: 'clinic-media-tab',
                title: 'Media',
                icon: 'bi bi-file-image',
                iconfill: 'bi bi-file-image-fill',
            },
            {
                id: 'clinic-stats-tab',
                title: 'Patient Stats',
                icon: 'bi bi-bar-chart',
                iconfill: 'bi bi-bar-chart-fill',
            },
        ],
    },
];

const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

const isTabVisibleForPms = (item, pmsType) => !item.requiresPms || item.requiresPms === pmsType;

const resolveTabIdFromSlug = (slug) => {
    if (!slug) {
        return DEFAULT_TAB_ID;
    }
    return TAB_ID_BY_SLUG[slug] || DEFAULT_TAB_ID;
};

const ClinicTabContainer = () => {
    const { clinicId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pmsType, setPmsType] = useState('NONE');

    const tabParam = searchParams.get('tab');
    const activeTab = resolveTabIdFromSlug(tabParam);

    const setActiveTab = useCallback(
        (tabId) => {
            const slug = TAB_SLUG_BY_ID[tabId] || DEFAULT_TAB_SLUG;
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (slug === DEFAULT_TAB_SLUG) {
                        next.delete('tab');
                    } else {
                        next.set('tab', slug);
                    }
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    useEffect(() => {
        let cancelled = false;
        clinicService
            .getPms(clinicId)
            .then((res) => {
                if (!cancelled) {
                    setPmsType(res.data?.pmsType || 'NONE');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPmsType('NONE');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [clinicId]);

    const visibleNavGroups = useMemo(
        () =>
            NAV_GROUPS.map((group) => ({
                ...group,
                items: group.items.filter((item) => isTabVisibleForPms(item, pmsType)),
            })).filter((group) => group.items.length > 0),
        [pmsType]
    );

    useEffect(() => {
        // Normalize unknown / outdated ?tab= values in the URL
        if (tabParam && !TAB_ID_BY_SLUG[tabParam]) {
            setActiveTab(DEFAULT_TAB_ID);
            return;
        }

        const pmsGatedTab = ALL_NAV_ITEMS.find(
            (item) => item.id === activeTab && item.requiresPms
        );
        if (pmsGatedTab && pmsGatedTab.requiresPms !== pmsType) {
            setActiveTab(DEFAULT_TAB_ID);
        }
    }, [pmsType, activeTab, tabParam, setActiveTab]);

    const handlePmsTypeChange = (nextPmsType) => {
        setPmsType(nextPmsType || 'NONE');
    };

    return (
        <div className="clinic-detail-layout">
            <aside className="clinic-detail-sidebar" aria-label="Clinic sections">
                {visibleNavGroups.map((group) => (
                    <div key={group.label} className="clinic-detail-nav-group">
                        <div className="clinic-detail-nav-group__label">{group.label}</div>
                        <ul className="clinic-detail-nav">
                            {group.items.map((item) => (
                                <TabNavItem
                                    key={item.id}
                                    id={item.id}
                                    title={item.title}
                                    icon={item.icon}
                                    iconfill={item.iconfill}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                />
                            ))}
                        </ul>
                    </div>
                ))}
            </aside>

            <div className="clinic-detail-content">
                <TabContent id="clinic-info-tab" activeTab={activeTab}>
                    <InfoTab onPmsTypeChange={handlePmsTypeChange} />
                </TabContent>
                <TabContent id="clinic-documents-tab" activeTab={activeTab}>
                    <DocumentTab />
                </TabContent>
                <TabContent id="clinic-status-tab" activeTab={activeTab}>
                    <AccountStatus />
                </TabContent>
                <TabContent id="clinic-sms-gateway" activeTab={activeTab}>
                    <SmsgatewayTab />
                </TabContent>
                <TabContent id="clinic-hour-tab" activeTab={activeTab}>
                    <HoursOfOperation />
                </TabContent>
                <TabContent id="clinic-social-tab" activeTab={activeTab}>
                    <SocialTab />
                </TabContent>
                <TabContent id="clinic-customization-tab" activeTab={activeTab}>
                    <CustomizationTab />
                </TabContent>
                <TabContent id="clinic-public-theme-tab" activeTab={activeTab}>
                    <PublicThemeTab />
                </TabContent>
                <TabContent id="clinic-lensbrand-tab" activeTab={activeTab}>
                    <LensTab />
                </TabContent>
                <TabContent id="clinic-settings-tab" activeTab={activeTab}>
                    <SettingsTab />
                </TabContent>
                <TabContent id="clinic-reminder-tab" activeTab={activeTab}>
                    <ReminderTab />
                </TabContent>
                <TabContent id="clinic-booking-tab" activeTab={activeTab}>
                    <BookingTab />
                </TabContent>
                <TabContent id="clinic-reputation-tab" activeTab={activeTab}>
                    <ReputationTab />
                </TabContent>
                <TabContent id="clinic-users-tab" activeTab={activeTab}>
                    <UsersTab setActiveTab={setActiveTab} activeTab={activeTab} />
                </TabContent>
                <TabContent id="clinic-user-reminder" activeTab={activeTab}>
                    <UserReminderTab setActiveTab={setActiveTab} />
                </TabContent>
                <TabContent id="clinic-user-settings" activeTab={activeTab}>
                    <ClinicUserSettings setActiveTab={setActiveTab} />
                </TabContent>
                <TabContent id="clinic-user-add" activeTab={activeTab}>
                    <ClinicUserAdd setActiveTab={setActiveTab} />
                </TabContent>
                <TabContent id="clinic-surveys-tab" activeTab={activeTab}>
                    <SurveyTab />
                </TabContent>
                <TabContent id="clinic-recalls-tab" activeTab={activeTab}>
                    <RecallsTab />
                </TabContent>
                <TabContent id="clinic-media-tab" activeTab={activeTab}>
                    <MediaTab />
                </TabContent>
                <TabContent id="clinic-history-tab" activeTab={activeTab}>
                    <HistoryTab />
                </TabContent>
                {pmsType === 'OPTOSYS' && (
                    <TabContent id="clinic-optosys-tab" activeTab={activeTab}>
                        <OptosysTab />
                    </TabContent>
                )}
                <TabContent id="clinic-stats-tab" activeTab={activeTab}>
                    <StatsTab />
                </TabContent>
            </div>
        </div>
    );
};

export default ClinicTabContainer;
